<script setup lang="ts">
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxPortal,
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxViewport,
} from "reka-ui";

const props = defineProps({
  placeholder: {
    type: String,
  },
  modelValue: {
    type: [Array, String] as PropType<
      string[] | string | number | number[] | undefined | null
    >,
  },
  options: {
    type: Array as PropType<any[]>,
    default: () => [],
  },
  groupedOptions: {
    type: Object as PropType<{ [key: string]: any[] }>,
    default: () => ({}),
  },
  showTags: {
    type: Boolean,
    default: true,
  },
  areOptionsGrouped: {
    type: Boolean,
    default: false,
  },
  valueProp: {
    type: String,
    default: "value",
  },
  label: {
    type: String,
    default: "label",
  },
  allowMultiple: {
    type: Boolean,
    default: false,
  },
  allowUserEntered: {
    type: Boolean,
    default: false,
  },
  canClear: {
    type: Boolean,
    default: false,
  },
  searchable: {
    type: Boolean,
    default: true,
  },
  allowAddAll: {
    type: Boolean,
    default: false,
  },
  blockOutsideFocus: {
    type: Boolean,
    default: false,
  },
  side: {
    type: String as PropType<"top" | "bottom" | "left" | "right">,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  preventDefault: {
    type: Boolean,
    default: false,
  },
  alwaysShowEmpty: {
    type: Boolean,
    default: false,
  },
  widthOverride: {
    type: String,
    default: undefined,
  },
});

const emit = defineEmits([
  "update:modelValue",
  "option-created",
  "option-clicked",
  "on-search",
]);

const secretRef = ref(0);

const customOption = computed(() => {
  if (
    props.allowUserEntered != true ||
    searchTerm.value == undefined ||
    searchTerm.value == ""
  ) {
    return false;
  }
  if (cleanOptions.value.length == 0 || typeof props.modelValue == "string") {
    return searchTerm.value;
  }
  return {
    [props.valueProp]: searchTerm.value,
    [props.label]: searchTerm.value,
    isCustom: true,
  };
});

const handleSelect = (values: any) => {
  secretRef.value++;
  if (customOption.value) {
    var customOptionValue =
      typeof customOption.value == "string"
        ? customOption.value
        : customOption.value[props.valueProp];

    var isSelected =
      values == customOptionValue || values.includes(customOptionValue);

    if (isSelected) {
      emit("option-created", customOption.value);
    }
  }

  if (props.allowMultiple) {
    emit("update:modelValue", Object.values(values));
  } else {
    emit("update:modelValue", values);
  }
};

const handleRemove = (id: string) => {
  if (props.allowMultiple != true) return;

  const newValues = (props.modelValue as string[]).filter((i) => i !== id);
  emit("update:modelValue", [...newValues]);
};

const selectedValues = computed(() => {
  return cleanOptions.value.filter((o) => {
    if (props.modelValue == undefined) return false;

    return props.modelValue.includes(optionValue.value(o));
  });
});

const optionValue = computed(() => {
  return (option: any) => {
    // If option is a string return it otherwise return option[valueProp]
    if (typeof option === "string" || option == undefined) return option;
    return option[props.valueProp];
  };
});

const matchingOption = computed(() => {
  return (val: string) => {
    return cleanOptions.value.find((o) => optionValue.value(o) === val);
  };
});

const optionLabel = computed(() => {
  return (option: any) => {
    // If option is a string return it otherwise return option[label]
    if (typeof option === "string") return option;

    if (option == undefined) return "";

    return option[props.label];
  };
});

const isOptionSelected = computed(() => {
  return (option: any) => {
    if (props.modelValue == undefined) return false;

    if (props.allowMultiple) {
      return props.modelValue.includes(optionValue.value(option));
    }

    return props.modelValue === optionValue.value(option);
  };
});

const handleClear = () => {
  if (props.allowMultiple) {
    emit("update:modelValue", []);
  } else {
    emit("update:modelValue", undefined);
  }
};

const searchTerm = ref("");

const isDropdownOpen = ref(false);

const cleanOptions = computed(() => {
  if (props.areOptionsGrouped != true) {
    return props.options;
  }

  return Object.values(props.groupedOptions).flat();
});

const groupOptions = computed(() => {
  return (group: string) => {
    if (props.areOptionsGrouped != true || group.length == 0) {
      return cleanOptions.value;
    }

    var searchTermLower = searchTerm.value.toLowerCase();

    return props.groupedOptions[group].filter((o) => {
      return (
        searchTermLower.length == 0 ||
        optionLabel.value(o).toLowerCase().includes(searchTermLower) ||
        group.toLowerCase().includes(searchTermLower)
      );
    });
  };
});

const groups = computed(() => {
  if (props.areOptionsGrouped != true) {
    if (props.options.length == 0) {
      return [];
    }

    return [""];
  }

  return Object.keys(props.groupedOptions).filter((g) => {
    return groupOptions.value(g).length > 0;
  });
});

const width = ref();
const triggerElement = ref();
const portalTarget = ref<HTMLElement>();
let resizeObserver: ResizeObserver | null = null;

const handleToggleOpen = (val: boolean) => {
  if (!val) return;

  searchTerm.value = "";

  const closestDialog = triggerElement.value?.closest('[role="dialog"]');
  portalTarget.value =
    closestDialog instanceof HTMLElement ? closestDialog : undefined;
};

onMounted(() => {
  resizeObserver = new ResizeObserver((entries) => {
    width.value = triggerElement.value?.clientWidth;
  });
  if (triggerElement.value) {
    resizeObserver.observe(triggerElement.value);
  }

  width.value = triggerElement.value?.clientWidth;
});

onBeforeUnmount(() => {
  if (resizeObserver && triggerElement.value) {
    resizeObserver.unobserve(triggerElement.value);
    resizeObserver.disconnect();
  }
});

const onOptionClicked = (event: MouseEvent, option: any) => {
  if (event.metaKey != true && event.ctrlKey != true) {
    if (props.preventDefault) {
      handleSelect(optionValue.value(option));
      event.preventDefault();
    }
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  emit("option-clicked", optionValue.value(option));
};

const onAddAll = () => {
  emit(
    "update:modelValue",
    cleanOptions.value.map((o) => optionValue.value(o))
  );
};

const clearAll = () => {
  emit("update:modelValue", []);
};

const isOldSafari = computed(() => {
  return useIsOlderSafari();
});

const handleFocusOutside = (event: any) => {
  if (props.blockOutsideFocus || isOldSafari.value) {
    event.preventDefault();
    event.stopPropagation();
  }
};

// Safari 15 has a bug where the pointerdown that opens a portalled popup can
// also be interpreted as an outside pointerdown and immediately dismiss it.
// Prevent outside pointerdown when it originates from within the trigger area,
// and optionally when blockOutsideFocus is requested by the caller.
const handlePointerDownOutside = (event: any) => {
  const target = event?.target as Node | null;

  // If the click originated from within the trigger element, don't dismiss.
  if (triggerElement.value && target && triggerElement.value.contains(target)) {
    event.preventDefault?.();
  }

  if (props.blockOutsideFocus) {
    event.preventDefault?.();
    event.stopPropagation?.();
  }
};

const optionsLastUpdated = ref(0);

watch(
  () => props.options,
  async () => {
    optionsLastUpdated.value = Date.now();
    await nextTick();
  }
);
</script>

<template>
  <ComboboxRoot
    class="relative w-full outline-none combobox-root"
    :multiple="allowMultiple"
    :model-value="modelValue"
    :reset-search-term-on-blur="false"
    :reset-search-term-on-select="false"
    @update:model-value="handleSelect"
    @update:open="handleToggleOpen"
    v-model:open="isDropdownOpen"
    :ignore-filter="areOptionsGrouped"
  >
    <ComboboxAnchor
      class="hover:bg-light/20 focus-within:outline focus-within:outline-focus focus-within:outline-2 focus-within:-outline-offset-2 min-h-[40px] text-input-1 w-full inline-flex items-center justify-between rounded border border-medium-tint leading-none gap-[5px] data-[placeholder]:text-medium cursor-pointer"
    >
      <ComboboxTrigger class="w-full form-select-input" tabindex="0">
        <slot name="trigger">
          <div
            class="flex items-start gap-2 w-full p-2 cursor-pointer"
            ref="triggerElement"
          >
            <BaseIconChevronUpDown class="h-[24px] shrink-0" />
            <span
              v-if="
                placeholder &&
                (modelValue == undefined ||
                  modelValue.length === 0 ||
                  showTags != true)
              "
              class="text-medium leading-[24px]"
            >
              {{ placeholder }}
            </span>
            <div
              class="flex items-center grow whitespace-nowrap overflow-hidden text-ellipsis"
            >
              <span
                class="text-input-1 text-left grow flex flex-row flex-wrap items-center gap-2"
                v-if="allowMultiple && showTags"
              >
                <div
                  v-for="option in selectedValues"
                  @click.stop.prevent="handleRemove(optionValue(option))"
                  :key="option[valueProp]"
                  class="text-input-1 flex flex-row items-center gap-[2px] bg-light rounded-md border border-medium-tint px-1 pr-2 h-[24px] box-border hover:bg-light/50"
                >
                  <BaseIconClose size="10" class="mt-[1px]" />
                  <span class="whitespace-nowrap">
                    {{ optionLabel(option) }}
                  </span>
                </div>
              </span>
              <span
                class="text-input-1 whitespace-nowrap overflow-hidden text-ellipsis"
                v-else
              >
                <slot name="singlelabel" :value="modelValue">
                  {{ optionLabel(matchingOption(modelValue as string)) }}
                </slot>
              </span>
            </div>
            <BaseIconButton
              v-if="
                canClear && modelValue != undefined && modelValue.length > 0
              "
              @click.stop.prevent="handleClear"
              color="default"
              :size="24"
              class="!p-0"
            >
              <BaseIconClose size="14" />
            </BaseIconButton>
          </div>
        </slot>
      </ComboboxTrigger>
    </ComboboxAnchor>

    <ComboboxPortal :to="portalTarget">
      <ComboboxContent
        @focusOutside="handleFocusOutside"
        @pointerDownOutside="handlePointerDownOutside"
        position="popper"
        class="z-[999999] flex flex-col min-h-0 bg-surface rounded border border-medium-tint max-h-[300px] pointer-events-auto mt-1 overflow-hidden"
        align="start"
        :side="side"
      >
        <ComboboxViewport
          class="w-full h-full overflow-hidden flex flex-col min-h-0"
        >
          <slot name="content">
            <div
              class="h-full flex flex-col min-h-0 max-h-[400px] overflow-hidden"
              :style="{
                width:
                  widthOverride != undefined ? widthOverride : width + 'px',
              }"
            >
              <div
                v-if="searchable || allowAddAll"
                class="shrink-0 flex flex-row border-b border-medium-tint items-center p-2 pr-0 h-[40px] focus-within:outline focus-within:outline-focus focus-within:outline-2 focus-within:-outline-offset-1 rounded-tl rounded-tr"
              >
                <div
                  v-if="searchable"
                  class="flex items-center gap-2 shrink-0 grow"
                >
                  <BaseIconSearch class="text-medium text-input-1" />
                  <ComboboxInput
                    class="form-select-search-input grow !bg-transparent outline-none h-full selection:bg-grass5 placeholder-medium pointer-events-auto text-input-1 placeholder:text-medium"
                    v-model="searchTerm"
                    placeholder="Filter"
                    @input="emit('on-search', searchTerm)"
                  />
                </div>
                <div
                  v-if="allowMultiple && allowAddAll"
                  class="flex items-center gap-2 shrink-0"
                >
                  <BaseTextButton @click="onAddAll" color="primary">
                    <BaseIconAddCircle class="mr-1" />
                    Add All
                  </BaseTextButton>
                  <BaseTextButton @click="clearAll" color="danger">
                    <BaseIconDelete class="mr-1" />
                    Clear All
                  </BaseTextButton>
                </div>
              </div>
              <ComboboxEmpty
                v-if="isLoading != true && allowUserEntered != true"
              >
                <div class="pt-2 px-1 flex items-center shrink-0">
                  <slot name="empty-results">
                    <BaseIconCircleDashed
                      class="text-medium text-input-1 mr-2"
                    />
                    <span class="text-input-1 text-medium"> No results </span>
                  </slot>
                </div>
              </ComboboxEmpty>
              <div v-if="isLoading == true">
                <div class="p-2 flex items-center shrink-0">
                  <BaseIconLoading class="mr-2" />
                  <span class="text-input-1 text-medium">Loading...</span>
                </div>
              </div>
              <div
                v-if="isLoading != true"
                class="p-1 grow flex flex-col min-h-0 overflow-auto"
                :key="optionsLastUpdated"
              >
                <ComboboxItem
                  v-if="customOption"
                  :key="optionValue(customOption)"
                  :value="optionValue(customOption)"
                  class="p-2 text-input-1 leading-none rounded flex flex-row items-center relative select-none data-[disabled]:text-mauve8 data-[disabled]:pointer-events-none data-[highlighted]:outline-none data-[highlighted]:bg-light cursor-pointer"
                  :class="{
                    'bg-primary/10 hover:bg-primary/5':
                      isOptionSelected(customOption),
                  }"
                >
                  <BaseCheckbox
                    v-if="allowMultiple"
                    class="mr-2 pointer-events-none mb-0"
                    :model-value="isOptionSelected(customOption)"
                  />
                  <span class="text-secondary mr-1"> (new) </span>
                  {{ optionLabel(customOption) }}
                </ComboboxItem>
                <ComboboxGroup
                  v-for="(group, index) in groups"
                  :class="{
                    'mt-2': index != 0,
                    'mt-1': index == 0 && customOption,
                  }"
                  :key="`${optionsLastUpdated}-${index}`"
                >
                  <ComboboxLabel v-if="group.length > 0">
                    <div class="w-full border-b pb-1 mb-1 px-2 font-bold">
                      {{ group }}
                    </div>
                  </ComboboxLabel>
                  <ComboboxItem
                    v-for="option in groupOptions(group)"
                    :key="`${optionsLastUpdated}-${optionValue(option)}`"
                    :value="optionValue(option)"
                    class="outline-none mb-1 last:mb-0"
                  >
                    <slot name="option" :option="option">
                      <div
                        @click="onOptionClicked($event, option)"
                        class="option-contents text-input-1 mb-1 last:mb-0 p-2 leading-none rounded flex items-center relative select-none data-[disabled]:text-mauve8 data-[disabled]:pointer-events-none cursor-pointer"
                        :class="{
                          'bg-primary/10 hover:bg-primary/5':
                            isOptionSelected(option),
                        }"
                      >
                        <BaseCheckbox
                          v-if="allowMultiple"
                          class="mr-2 pointer-events-none mb-[1px]"
                          :model-value="isOptionSelected(option)"
                          size="input"
                        />
                        <span class="text-input-1">
                          {{ optionLabel(option) }}
                        </span>
                      </div>
                    </slot>
                  </ComboboxItem>
                </ComboboxGroup>
              </div>
              <div
                v-if="
                  (alwaysShowEmpty ||
                    (options.length == 0 &&
                      Object.keys(groupedOptions).length == 0)) &&
                  searchTerm.length == 0 &&
                  isLoading != true
                "
                :key="optionsLastUpdated"
                :class="{
                  'pb-2 border-t-2': alwaysShowEmpty,
                }"
              >
                <div class="pt-2 px-1 flex items-center shrink-0">
                  <slot name="empty-results">
                    <BaseIconCircleDashed
                      class="text-medium text-input-1 mr-2"
                    />
                    <span class="text-input-1 text-medium">No results</span>
                  </slot>
                </div>
              </div>
            </div>
          </slot>
        </ComboboxViewport>
      </ComboboxContent>
    </ComboboxPortal>
  </ComboboxRoot>
</template>

<style>
div[role="listbox"] > div[role="presentation"] {
  padding: 0 !important;
}

.combobox-root button {
  outline: none !important;
}

div[role="listbox"] > div[role="presentation"] input::placeholder {
  @apply text-input-1;
}

[data-highlighted] .option-contents {
  outline: none;
  @apply bg-light;
}
</style>
